import * as moment from 'moment';

import type Store from '~/types/Store';

import * as shopifyIcon from '~/assets/shops/20/Shopify.png';
import * as squareIcon from '~/assets/shops/20/square.png';
import * as amazonLogoSmall from '~/assets/shops/icons/amazon.svg';
import * as bigCommerceLogoSmall from '~/assets/shops/icons/bigcommerce.svg';
import * as channelAdvisorLogoSmall from '~/assets/shops/icons/channeladvisor.svg';
import * as ebayLogoSmall from '~/assets/shops/icons/ebay.svg';
import * as ePagesLogoSmall from '~/assets/shops/icons/epages.svg';
import * as etsyLogoSmall from '~/assets/shops/icons/etsy.svg';
import * as facebookLogoSmall from '~/assets/shops/icons/facebook.svg';
import * as godaddyLogoSmall from '~/assets/shops/icons/godaddy.svg';
import * as magentoLogoSmall from '~/assets/shops/icons/magento.svg';
import * as magentoTwoLogoSmall from '~/assets/shops/icons/magento2.svg';
import * as shopifyLogoSmall from '~/assets/shops/icons/shopify.svg';
import * as spreecommerceLogoSmall from '~/assets/shops/icons/spree.svg';
import * as squareLogoSmall from '~/assets/shops/icons/square.svg';
import * as squarespaceLogoSmall from '~/assets/shops/icons/squarespace.svg';
import * as stripeLogoSmall from '~/assets/shops/icons/stripe.svg';
import * as tiktokLogoSmall from '~/assets/shops/icons/tiktok.svg';
import * as walmartLogoSmall from '~/assets/shops/icons/walmart.svg';
import * as weeblyLogoSmall from '~/assets/shops/icons/weebly.svg';
import * as wixLogoSmall from '~/assets/shops/icons/wix.svg';
import * as wooCommerceLogoSmall from '~/assets/shops/icons/woocommerce.svg';
import * as amazonLogo from '~/assets/shops/logos/amazon.svg';
import * as bigCommerceLogo from '~/assets/shops/logos/bigcommerce.svg';
import * as channelAdvisorLogo from '~/assets/shops/logos/channeladvisor.svg';
import * as ebayLogo from '~/assets/shops/logos/ebay.svg';
import * as ePagesLogo from '~/assets/shops/logos/epages.svg';
import * as etsyLogo from '~/assets/shops/logos/etsy.svg';
import * as facebookLogo from '~/assets/shops/logos/facebook.svg';
import * as godaddyLogo from '~/assets/shops/logos/godaddy.svg';
import * as magentoLogo from '~/assets/shops/logos/magento.svg';
import * as magentoTwoLogo from '~/assets/shops/logos/magento2.svg';
import * as shopifyLogo from '~/assets/shops/logos/shopify.svg';
import * as spreeLogo from '~/assets/shops/logos/spree.svg';
import * as squareLogo from '~/assets/shops/logos/square.svg';
import * as squarespaceLogo from '~/assets/shops/logos/squarespace.svg';
import * as stripeLogo from '~/assets/shops/logos/stripe.svg';
import * as tiktokLogo from '~/assets/shops/logos/tiktok.svg';
import * as walmartLogo from '~/assets/shops/logos/walmart.svg';
import * as weeblyLogo from '~/assets/shops/logos/weebly.svg';
import * as wixLogo from '~/assets/shops/logos/wix.svg';
import * as wooCommerceLogo from '~/assets/shops/logos/woocommerce.svg';
import * as shopifySvg from '~/assets/shops/shopify/logo-mark.svg';
import * as squareSvg from '~/assets/shops/vector/square.svg';
import { StoreName, StoreStatusName } from '~/types/Store';

const shippoStore: Store = {
  displayName: '',
  expirationDate: null,
  isAutoFulfill: false,
  isFulfillEmailEnabled: false,
  isImportPosOrdersEnabled: false,
  lastSyncTime: moment(),
  name: '',
  objectCreated: moment(),
  objectId: '1', // fake object id
  status: {
    description: 'Connected',
    name: StoreStatusName.Connected,
  },
  statusDetails: StoreStatusName.Connected,
  storePlatform: {
    name: StoreName.Shippo,
  },
  website: '',
};

const csvImportStore: Store = {
  displayName: '',
  expirationDate: null,
  isAutoFulfill: false,
  isFulfillEmailEnabled: false,
  isImportPosOrdersEnabled: false,
  lastSyncTime: moment(),
  name: '',
  objectCreated: moment(),
  objectId: '2',
  status: {
    description: 'Connected',
    name: StoreStatusName.Connected,
  },
  statusDetails: StoreStatusName.Connected,
  storePlatform: {
    name: StoreName.CSV,
  },
  website: '',
};

// Display helper
interface StoreAsset {
  canEdit: boolean;
  connectDocLink?: string;
  connectLink: string;
  connectPropsReq?: boolean;
  connectRoute?: string;
  hasFulfillmentOptions?: boolean;
  icon?: string;
  logoBig: string;
  logoMark?: string;
  logoSmall: string;
  name: StoreName;
  supportMultiStore: boolean;
}

const amazon: StoreAsset = {
  canEdit: true,
  connectLink: `${CONFIG.djangoBaseUrl}/amazon/setup`,
  connectRoute: '/connect/amazon',
  logoBig: amazonLogo,
  logoSmall: amazonLogoSmall,
  name: StoreName.Amazon,
  supportMultiStore: true,
};

const bigcommerce: StoreAsset = {
  canEdit: true,
  connectLink: `${CONFIG.djangoBaseUrl}/bigcommerce/setup`,
  logoBig: bigCommerceLogo,
  logoSmall: bigCommerceLogoSmall,
  name: StoreName.Bigcommerce,
  supportMultiStore: true,
};

const channelAdvisor: StoreAsset = {
  canEdit: false,
  connectLink: `${CONFIG.djangoBaseUrl}/channeladvisor/setup`,
  logoBig: channelAdvisorLogo,
  logoSmall: channelAdvisorLogoSmall,
  name: StoreName.ChannelAdvisor,
  supportMultiStore: false,
};

const ebay: StoreAsset = {
  canEdit: true,
  connectLink: `${CONFIG.djangoBaseUrl}/ebay/setup`,
  logoBig: ebayLogo,
  logoSmall: ebayLogoSmall,
  name: StoreName.eBay,
  supportMultiStore: true,
};

const tiktok: StoreAsset = {
  canEdit: true,
  connectLink: `${CONFIG.djangoBaseUrl}/tiktok/setup`,
  logoBig: tiktokLogo,
  logoSmall: tiktokLogoSmall,
  name: StoreName.TikTok,
  supportMultiStore: true,
};

const ePages: StoreAsset = {
  canEdit: true,
  connectLink: `${CONFIG.djangoBaseUrl}/epages/setup`,
  logoBig: ePagesLogo,
  logoSmall: ePagesLogoSmall,
  name: StoreName.ePages,
  supportMultiStore: true,
};

const etsy: StoreAsset = {
  canEdit: true,
  connectLink: `${CONFIG.djangoBaseUrl}/etsy/setup`,
  connectRoute: '/connect/etsy',
  logoBig: etsyLogo,
  logoSmall: etsyLogoSmall,
  name: StoreName.Etsy,
  supportMultiStore: true,
};

const facebook: StoreAsset = {
  canEdit: true,
  connectLink: `${CONFIG.djangoBaseUrl}/channels/facebook/setup`,
  connectRoute: '/connect/facebook',
  logoBig: facebookLogo,
  logoSmall: facebookLogoSmall,
  name: StoreName.Facebook,
  supportMultiStore: false,
};

const goDaddy: StoreAsset = {
  canEdit: true,
  connectLink: `${CONFIG.djangoBaseUrl}/onlinestore/setup`,
  logoBig: godaddyLogo,
  logoSmall: godaddyLogoSmall,
  name: StoreName.Godaddy,
  supportMultiStore: false,
};

const magento: StoreAsset = {
  canEdit: true,
  connectLink: `${CONFIG.djangoBaseUrl}/magento/setup`,
  hasFulfillmentOptions: true,
  logoBig: magentoLogo,
  logoSmall: magentoLogoSmall,
  name: StoreName.Magento,
  supportMultiStore: false,
};

const magentoTwo: StoreAsset = {
  canEdit: true,
  connectLink: `${CONFIG.djangoBaseUrl}/magento_two/setup`,
  hasFulfillmentOptions: true,
  logoBig: magentoTwoLogo,
  logoSmall: magentoTwoLogoSmall,
  name: StoreName.Magento_2,
  supportMultiStore: true,
};

const shopify: StoreAsset = {
  canEdit: false,
  connectLink: `${CONFIG.djangoBaseUrl}/shopify/setup`,
  connectPropsReq: true,
  connectRoute: '/connect/shopify',
  hasFulfillmentOptions: true,
  icon: shopifyIcon,
  logoBig: shopifyLogo,
  logoMark: shopifySvg,
  logoSmall: shopifyLogoSmall,
  name: StoreName.Shopify,
  supportMultiStore: true,
};

const spreecommerce: StoreAsset = {
  canEdit: true,
  connectLink: `${CONFIG.djangoBaseUrl}/spreecommerce/setup`,
  logoBig: spreeLogo,
  logoSmall: spreecommerceLogoSmall,
  name: StoreName.Spreecommerce,
  supportMultiStore: true,
};

const squarespace: StoreAsset = {
  canEdit: false,
  connectLink: `${CONFIG.djangoBaseUrl}/squarespace/setup`,
  logoBig: squarespaceLogo,
  logoSmall: squarespaceLogoSmall,
  name: StoreName.Squarespace,
  supportMultiStore: true,
};

const stripe: StoreAsset = {
  canEdit: true,
  connectLink: `${CONFIG.djangoBaseUrl}/stripe/setup`,
  logoBig: stripeLogo,
  logoSmall: stripeLogoSmall,
  name: StoreName.Stripe,
  supportMultiStore: false,
};

const walmart: StoreAsset = {
  canEdit: true,
  connectDocLink:
    'https://support.goshippo.com/hc/en-us/articles/18991472509339-Connect-Your-Walmart-Store-to-Shippo',
  connectLink: `${CONFIG.djangoBaseUrl}/connect/walmart`,
  connectRoute: '/connect/walmart',
  logoBig: walmartLogo,
  logoSmall: walmartLogoSmall,
  name: StoreName.Walmart,
  supportMultiStore: true,
};

const weebly: StoreAsset = {
  canEdit: true,
  connectLink: `${CONFIG.djangoBaseUrl}/weebly/setup`,
  logoBig: weeblyLogo,
  logoSmall: weeblyLogoSmall,
  name: StoreName.Weebly,
  supportMultiStore: false,
};

const wix: StoreAsset = {
  canEdit: false,
  connectLink: `${CONFIG.djangoBaseUrl}/wix/setup`,
  connectRoute: '/connect/wix',
  logoBig: wixLogo,
  logoSmall: wixLogoSmall,
  name: StoreName.Wix,
  supportMultiStore: true,
};

const wooCommerce: StoreAsset = {
  canEdit: false,
  connectLink: `${CONFIG.djangoBaseUrl}/woocommercev2/setup`,
  logoBig: wooCommerceLogo,
  logoSmall: wooCommerceLogoSmall,
  name: StoreName.WooCommerce,
  supportMultiStore: true,
};

const square: StoreAsset = {
  canEdit: false,
  connectLink: `${CONFIG.djangoBaseUrl}/channels/square/setup`,
  icon: squareIcon,
  logoBig: squareLogo,
  logoMark: squareSvg,
  logoSmall: squareLogoSmall,
  name: StoreName.Square,
  supportMultiStore: true,
};

const storeAssets: { [storeName: string]: StoreAsset } = {
  [StoreName.Amazon]: amazon,
  [StoreName.Bigcommerce]: bigcommerce,
  [StoreName.ChannelAdvisor]: channelAdvisor,
  [StoreName.Etsy]: etsy,
  [StoreName.Facebook]: facebook,
  [StoreName.Godaddy]: goDaddy,
  [StoreName.Magento]: magento,
  [StoreName.Magento_2]: magentoTwo,
  [StoreName.Shopify]: shopify,
  [StoreName.Spreecommerce]: spreecommerce,
  [StoreName.Square]: square,
  [StoreName.Squarespace]: squarespace,
  [StoreName.Stripe]: stripe,
  [StoreName.TikTok]: tiktok,
  [StoreName.Walmart]: walmart,
  [StoreName.Weebly]: weebly,
  [StoreName.Wix]: wix,
  [StoreName.WooCommerce]: wooCommerce,
  [StoreName.eBay]: ebay,
  [StoreName.ePages]: ePages,
};

const storeNames: readonly string[] = Object.values(StoreName);

const storesInOrder: StoreName[] = [
  StoreName.Shopify,
  StoreName.Godaddy,
  StoreName.Wix,
  StoreName.Magento,
  StoreName.Magento_2,
  StoreName.Bigcommerce,
  StoreName.Etsy,
  StoreName.Spreecommerce,
  StoreName.Weebly,
  StoreName.Stripe,
  StoreName.eBay,
  StoreName.Amazon,
  StoreName.WooCommerce,
  StoreName.Squarespace,
  StoreName.ChannelAdvisor,
  StoreName.ePages,
  StoreName.Square,
  StoreName.Facebook,
  StoreName.Walmart,
];

export { StoreAsset, csvImportStore, shippoStore, storeAssets, storeNames, storesInOrder };
